export const SELECTION_OLDEST_FIRST = 'oldest_first';

export const POOL_ACTION = {
  CLAIM: 'claim',
  RECOVER: 'recover',
};

export const CLAIM_BLOCK = {
  PENDING_RECOVERIES: 'PendingRecoveries',
  LIMIT_REACHED: 'LimitReached',
  NOT_OLDEST: 'NotOldest',
};

export const DROP_RESULT = {
  MOVE: 'move',
  RECOVER: 'recover',
  BLOCKED: 'blocked',
};

export function isQuotaSpent(block) {
  return (
    Boolean(block) &&
    !block.can_bypass_limits &&
    block.daily_limit != null &&
    block.remaining != null &&
    block.remaining <= 0
  );
}

function poolBlockReason(block) {
  if (block.pending_recoveries > 0) return CLAIM_BLOCK.PENDING_RECOVERIES;
  if (isQuotaSpent(block)) return CLAIM_BLOCK.LIMIT_REACHED;
  return null;
}

// The backend lists a pool's contacts by lead_status_change_date ascending,
// so the first foreign contact loaded is the oldest one.
export function oldestForeignId(contacts) {
  const first = (contacts || []).find((contact) => !contact.is_own);
  return first ? first.id : null;
}

export function claimBlockReason(contact, block, oldestId) {
  if (!block || contact.is_own || block.can_bypass_limits) return null;
  const reason = poolBlockReason(block);
  if (reason) return reason;
  if (
    block.selection_mode === SELECTION_OLDEST_FIRST &&
    contact.id !== oldestId
  ) {
    return CLAIM_BLOCK.NOT_OLDEST;
  }
  return null;
}

export function claimDisabledReason(contact, block) {
  if (!block || contact.is_own || contact.can_claim) return null;
  const reason = poolBlockReason(block);
  if (reason) return reason;
  if (block.selection_mode === SELECTION_OLDEST_FIRST) {
    return CLAIM_BLOCK.NOT_OLDEST;
  }
  return null;
}

export function recomputeCanClaim(contacts, block) {
  const oldestId = oldestForeignId(contacts);
  return (contacts || []).map((contact) => ({
    ...contact,
    can_claim:
      !contact.is_own && claimBlockReason(contact, block, oldestId) === null,
  }));
}

// Contacts to recover go first; each half keeps the backend's oldest first order.
export function ownFirst(contacts) {
  const list = contacts || [];
  return [
    ...list.filter((contact) => contact.is_own),
    ...list.filter((contact) => !contact.is_own),
  ];
}

// Exempt users (admins, can_reassign_contacts_leads) see pool contacts with
// the regular card and row.
export function usesPoolCard(block) {
  return Boolean(block) && !block.can_bypass_limits;
}

// Like the backend, a pool contact is own when its agent is the user: own ones
// wait to be recovered and block claims, foreign ones can be claimed.
export function admitToPool(block, contacts, currentUserId) {
  if (!usesPoolCard(block)) return { block, contacts };
  const admitted = contacts.map((contact) => ({
    ...contact,
    is_own: currentUserId != null && contact.agent_id === currentUserId,
    can_claim: false,
  }));
  const own = admitted.filter((contact) => contact.is_own).length;
  return {
    block: {
      ...block,
      pending_recoveries: (block.pending_recoveries || 0) + own,
    },
    contacts: admitted,
  };
}

// Recovers count towards the daily quota, so a spent quota blocks them too.
export function recoverDisabledReason(contact, block) {
  if (!block || !contact?.is_own) return null;
  return isQuotaSpent(block) ? CLAIM_BLOCK.LIMIT_REACHED : null;
}

export function canDragFromPool(contact, block) {
  if (block?.can_bypass_limits) return true;
  return Boolean(contact?.is_own) && !isQuotaSpent(block);
}

export function resolveDrop({ contact, sourceBlock, targetColumnId }) {
  if (!sourceBlock || sourceBlock.can_bypass_limits) return DROP_RESULT.MOVE;
  if (
    contact?.is_own &&
    !isQuotaSpent(sourceBlock) &&
    targetColumnId === sourceBlock.recover_target_status_id
  ) {
    return DROP_RESULT.RECOVER;
  }
  return DROP_RESULT.BLOCKED;
}

export function poolInfoKey(block) {
  const limit = block.daily_limit != null ? 'Limited' : 'Unlimited';
  const order = block.selection_mode === SELECTION_OLDEST_FIRST ? 'Oldest' : '';
  return `${limit}${order}`;
}

export function applyResultToColumns(
  columns,
  { statusId, contactId, sharkTank, contact }
) {
  const targetId = sharkTank?.recover_target_status_id;
  const movedToVisibleColumn =
    Boolean(contact) &&
    columns.some((column) => column.lead_status.id === targetId);
  let removed = 0;
  const next = columns.map((column) => {
    if (column.lead_status.id === statusId) {
      const remaining = column.contacts.filter((item) => item.id !== contactId);
      removed = column.contacts.length - remaining.length;
      return {
        ...column,
        total_contacts: column.total_contacts - removed,
        contacts: recomputeCanClaim(remaining, sharkTank),
        lead_status: { ...column.lead_status, shark_tank: sharkTank },
      };
    }
    const alreadyThere = column.contacts.some((item) => item.id === contactId);
    if (column.lead_status.id === targetId && contact && !alreadyThere) {
      return {
        ...column,
        total_contacts: column.total_contacts + 1,
        contacts: [contact, ...column.contacts],
      };
    }
    return column;
  });
  return { columns: next, removed, movedToVisibleColumn };
}

export function applyResultToGroups(
  groups,
  { statusId, contactId, sharkTank, contact }
) {
  const targetId = sharkTank?.recover_target_status_id;
  return groups.map((group) => {
    if (group.id === statusId) {
      const remaining = group.contacts.filter((item) => item.id !== contactId);
      return {
        ...group,
        sharkTank,
        contacts: recomputeCanClaim(remaining, sharkTank),
      };
    }
    const alreadyThere = group.contacts.some((item) => item.id === contactId);
    if (group.id === targetId && contact && !alreadyThere) {
      return { ...group, contacts: [contact, ...group.contacts] };
    }
    return group;
  });
}

export function applyResultToCounts(counts, { statusId, sharkTank }) {
  if (!counts) return counts;
  const targetId = sharkTank?.recover_target_status_id;
  const byStatus = { ...counts.byStatus };
  if (typeof byStatus[statusId] === 'number') byStatus[statusId] -= 1;
  const targetCounted = typeof byStatus[targetId] === 'number';
  if (targetCounted) byStatus[targetId] += 1;
  // A visible pool is outside the header until the user can reassign.
  const sourceInHeader = !usesPoolCard(sharkTank);
  let { total } = counts;
  if (typeof total === 'number') {
    if (sourceInHeader && !targetCounted) total -= 1;
    if (!sourceInHeader && targetCounted) total += 1;
  }
  return { ...counts, byStatus, total };
}
